import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const AlertSettingsPanel = ({ onClose }) => {
  const [settings, setSettings] = useState({
    viralContent: true,
    engagementSpikes: true,
    mentions: true,
    competitorActivity: false,
    trendingHashtags: true,
    optimalTiming: true,
    engagementThreshold: '50',
    notificationFrequency: 'realtime'
  });

  const frequencyOptions = [
    { value: 'realtime', label: 'Real-time' },
    { value: 'hourly', label: 'Hourly Digest' },
    { value: 'daily', label: 'Daily Summary' },
    { value: 'custom', label: 'Custom Schedule' }
  ];

  const handleSave = () => {
    alert('Alert settings saved successfully!');
    onClose?.();
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-lg border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            Alert Preferences
          </h2>
        </div>
        <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
      </div>

      <div className="space-y-6">
        {/* Alert Types */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Alert Types</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={settings?.viralContent}
                onChange={(checked) => setSettings({ ...settings, viralContent: checked })}
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Viral Content Detection</label>
                <p className="text-xs text-muted-foreground">Get notified when your posts show viral potential</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={settings?.engagementSpikes}
                onChange={(checked) => setSettings({ ...settings, engagementSpikes: checked })}
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Engagement Spikes</label>
                <p className="text-xs text-muted-foreground">Alert on unusual increases in engagement activity</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={settings?.mentions}
                onChange={(checked) => setSettings({ ...settings, mentions: checked })}
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Mentions & Tags</label>
                <p className="text-xs text-muted-foreground">Notify when someone mentions or tags you</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={settings?.competitorActivity}
                onChange={(checked) => setSettings({ ...settings, competitorActivity: checked })}
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Competitor Activity</label>
                <p className="text-xs text-muted-foreground">Track posts from industry leaders and competitors</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={settings?.trendingHashtags}
                onChange={(checked) => setSettings({ ...settings, trendingHashtags: checked })}
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Trending Hashtags</label>
                <p className="text-xs text-muted-foreground">Alert on relevant trending topics in your industry</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={settings?.optimalTiming}
                onChange={(checked) => setSettings({ ...settings, optimalTiming: checked })}
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Optimal Posting Windows</label>
                <p className="text-xs text-muted-foreground">Remind when it's the best time to post</p>
              </div>
            </div>
          </div>
        </div>

        {/* Thresholds */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Engagement Thresholds</h3>
          <Input
            label="Minimum engagement for alerts"
            type="number"
            value={settings?.engagementThreshold}
            onChange={(e) => setSettings({ ...settings, engagementThreshold: e?.target?.value })}
            description="Only alert when engagement exceeds this number"
            placeholder="50"
          />
        </div>

        {/* Notification Frequency */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Notification Frequency</h3>
          <Select
            value={settings?.notificationFrequency}
            onChange={(value) => setSettings({ ...settings, notificationFrequency: value })}
            options={frequencyOptions}
            label="How often should we notify you?"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button onClick={handleSave} className="flex-1">
            Save Settings
          </Button>
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertSettingsPanel;